import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { User } from "@/types";

export const __DEV__ = import.meta.env.DEV;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const navPath = (root: string, item?: string) =>
  `${root}${item ? `${item}` : ""}`;

export const theFullName = (user: User) =>
  `${user.first_name || ""} ${user.last_name || ""}`;

/**
 * Builds API parameters with Laravel-style filtering support
 * @param baseParams - Base parameters like page, per_page, etc.
 * @param filters - Object containing filter key-value pairs
 * @returns Object with properly formatted API parameters
 */
export const buildApiParams = (
  baseParams: Record<string, any> = {},
  filters: Record<string, string | number | boolean | undefined> = {}
): Record<string, any> => {
  const apiParams: Record<string, any> = {};

  // Add base parameters, but skip page=1, undefined per_page, and per_page=15 (server default)
  Object.entries(baseParams).forEach(([key, value]) => {
    if (key === 'page' && value === 1) {
      // Skip page=1 (server default)
      return;
    }
    if (key === 'per_page' && (value === undefined || value === 15)) {
      // Skip undefined per_page or per_page=15 (server default)
      return;
    }
    if (value !== undefined && value !== null) {
      apiParams[key] = value;
    }
  });

  // Add filters with Laravel-style format
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== null) {
      apiParams[`filter[${key}]`] = value;
    }
  });

  return apiParams;
};

/**
 * Truncates text to a specified maximum length
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 50)
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Copies text to clipboard and shows a toast notification
 * @param text - The text to copy to clipboard
 * @param toastMessage - Custom toast message (optional)
 * @returns Promise that resolves when copy operation is complete
 */
export const copyToClipboard = async (
  text: string,
  toastMessage?: string
): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);

    // Import toast dynamically to avoid circular dependencies
    const { toast } = await import('react-toastify');

    if (toastMessage) {
      toast.success(toastMessage);
    } else {
      toast.success('Copied to clipboard');
    }
  } catch (err) {
    const { toast } = await import('react-toastify');
    toast.error('Failed to copy to clipboard');
  }
};
